import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { TextareaInput, TextInput } from "@/components/ui/Field";
import {
  flattenBillingQuestionsToAnswerRows,
  groupMaterialAnswerRows,
  type BillingAnswerRow,
} from "@/features/timesheetFlow/billingQuestionAnswerRows";
import {
  getActionLog,
  listBillingQuestions,
  appendTimesheetMaterialPositions,
  deleteTimesheetMaterialPositions,
  respondBillingQuestion,
  type ActionLogEntry,
  type BillingQuestion,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { BillingAnsweredRowCard } from "@/features/timesheetFlow/components/BillingAnsweredRowCard";
import { formatBillingMaterialLine } from "@/features/timesheetFlow/timesheetBillingUtils";
import type { TimesheetMaterialRecord, TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  buildBillingAnswerText,
  parseAnswerArticles,
  answerTextWithoutArticles,
} from "@/features/timesheetFlow/billingAnswerDisplay";
import {
  buildMergedLeistungFromPositions,
  editorPositionPricingPayload,
  editorPositionToMaterial,
  formatEditorPositionLine,
  hydrateAnswerPositions,
  resolveAnswerAppendedMaterialIds,
  resolveAnsweredArticleDisplay,
} from "@/features/timesheetFlow/billingAnswerPositionUtils";
import {
  buildBillingInvoicePreviewDoc,
  buildBillingInvoicePreviewPositions,
} from "@/features/timesheetFlow/billingInvoicePreview";
import { useCurrentUser, userCanSeePrices } from "@/lib/auth";
import { formatCurrency, positionEpNetto } from "@/lib/documentCalc";
import { getTimesheetDisplayNumber } from "@/pages/stundennachweise/timesheetFilterLogic";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import { InvoiceDraftContextButtons } from "@/pages/dokumente/InvoiceDraftContextButtons";
import { DocumentPositionEditDialog } from "@/pages/dokumente/DocumentPositionEditDialog";
import { DocumentHistorischePositionenDialog } from "@/pages/dokumente/DocumentHistorischePositionenDialog";
import { DocumentPreviewPanel } from "@/pages/dokumente/DocumentPreviewPanel";
import {
  createBlockPosition,
  createPositionFromHistorie,
  normalizePositionTyp,
  positionTotals,
  syncLeistungEinzelpreis,
  type EditorPosition,
} from "@/pages/dokumente/documentPositionModel";
import {
  filterGdLohnartenForUser,
  getGdLohnarten,
  type DocumentChainStep,
  type GdLohnart,
  type HistorischeDokumentPosition,
} from "@/lib/dokumente";
import { saveOutstandingMaterialPricing } from "@/lib/outstandingBalances";
import { getOrder } from "@/lib/projectOverview";

function parseBillingPriceInput(raw: string): string {
  const text = String(raw || "").trim();
  if (!text) return "";
  const normalized = text.includes(",")
    ? text.replace(/\./g, "").replace(",", ".")
    : text;
  const num = Number(normalized);
  return Number.isFinite(num) ? String(num) : "";
}

function formatPeriod(ts: TimesheetRecord): string {
  const fmt = (iso?: string | null) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("de-DE");
  };
  const from = fmt(ts.date_from);
  const to = fmt(ts.date_to);
  if (from && to) return from === to ? from : `${from} – ${to}`;
  return from || to || "";
}

function formatDateTimeLabel(value?: string | null): string {
  const parsed = new Date(value || "");
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BillingAnswerPositionList({
  positions,
  canSeePrices,
  onAddArtikel,
  onAddLeistung,
  onSearchHistorie,
  onEdit,
  onRemove,
}: {
  positions: EditorPosition[];
  canSeePrices: boolean;
  onAddArtikel: () => void;
  onAddLeistung: () => void;
  onSearchHistorie: () => void;
  onEdit: (index: number) => void;
  onRemove: (index: number) => void;
}) {
  return (
    <div className="mt-4 rounded-ios border border-separator bg-card/60 p-3 sm:p-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h5 className="text-sm font-semibold text-label">Artikel / Leistung hinterlegen</h5>
          <p className="mt-0.5 text-xs text-label-secondary">
            Wie im Dokument: Artikel oder Leistung kalkulieren bzw. frühere Positionen übernehmen.
            Mehrere Positionen werden als interne Unterpositionen der Ursprungsposition gespeichert.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="secondary" size="sm" onClick={onAddArtikel}>
            Artikel
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onAddLeistung}>
            Leistung
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={onSearchHistorie}>
            Frühere Positionen
          </Button>
        </div>
      </div>
      {positions.length === 0 ? (
        <p className="text-sm text-label-tertiary">Noch keine Position hinterlegt.</p>
      ) : (
        <ul className="space-y-2">
          {positions.map((position, index) => {
            const typ = String(position.position_typ || "").toLowerCase();
            const typLabel = typ === "leistung" ? "Leistung" : "Artikel";
            const synced =
              normalizePositionTyp(position.position_typ) === "leistung"
                ? syncLeistungEinzelpreis(position)
                : position;
            const line = formatEditorPositionLine(synced);
            const ep = canSeePrices ? formatCurrency(positionEpNetto(synced)) : null;
            const total = canSeePrices
              ? formatCurrency(positionTotals(synced).zeilen_netto)
              : null;
            return (
              <li
                key={position.localId || `${line}-${index}`}
                className="flex items-start justify-between gap-3 rounded-ios bg-fill-tertiary/40 px-3 py-2"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left"
                  onClick={() => onEdit(index)}
                >
                  <span className="block text-[11px] font-semibold uppercase tracking-wide text-label-tertiary">
                    {typLabel}
                  </span>
                  <span className="text-sm leading-snug text-label">{line}</span>
                  {ep && total ? (
                    <span className="mt-0.5 block text-xs text-label-secondary">
                      EP {ep} · {total}
                    </span>
                  ) : null}
                </button>
                <button
                  type="button"
                  className="shrink-0 text-xs font-medium text-status-red hover:underline"
                  onClick={() => onRemove(index)}
                >
                  Entfernen
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function OpenAnswerCard({
  row,
  draftAnswer,
  draftPrice,
  draftPositions,
  busy,
  editing,
  canSeePrices,
  onAnswerChange,
  onPriceChange,
  onAddArtikel,
  onAddLeistung,
  onSearchHistorie,
  onEditPosition,
  onRemovePosition,
  onSave,
  onCancel,
}: {
  row: BillingAnswerRow;
  draftAnswer: string;
  draftPrice: string;
  draftPositions: EditorPosition[];
  busy: boolean;
  editing?: boolean;
  canSeePrices: boolean;
  onAnswerChange: (value: string) => void;
  onPriceChange: (value: string) => void;
  onAddArtikel: () => void;
  onAddLeistung: () => void;
  onSearchHistorie: () => void;
  onEditPosition: (index: number) => void;
  onRemovePosition: (index: number) => void;
  onSave: () => void;
  onCancel?: () => void;
}) {
  const showPriceField = canSeePrices && row.showPriceField;
  const canSave =
    draftAnswer.trim().length > 0 ||
    draftPositions.length > 0 ||
    (showPriceField && draftPrice.trim().length > 0);

  return (
    <article
      className={clsx(
        "rounded-ios-lg border p-4 sm:p-5",
        editing
          ? "border-accent/35 bg-accent/5"
          : "border-amber-500/30 bg-amber-500/5",
      )}
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <h4 className="whitespace-pre-wrap text-base font-semibold leading-snug text-label sm:text-lg">
          {row.questionText}
        </h4>
        <span
          className={clsx(
            "shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide",
            editing
              ? "bg-accent/20 text-accent"
              : "bg-amber-500/20 text-amber-900 dark:text-amber-100",
          )}
        >
          {editing ? "Bearbeiten" : "Offen"}
        </span>
      </div>
      {row.materialLine ? (
        <p className="mb-3 text-sm font-semibold text-label">
          {row.materialLine.startsWith("Pos.") ? row.materialLine : `Bezug: ${row.materialLine}`}
        </p>
      ) : null}
      {row.createdBy || row.createdAt ? (
        <p className="mb-3 text-xs text-label-tertiary">
          {[row.createdBy ? `von ${row.createdBy}` : null, row.createdAt ? formatDateTimeLabel(row.createdAt) : null]
            .filter(Boolean)
            .join(" · ")}
        </p>
      ) : null}

      <TextareaInput
        label="Deine Antwort"
        rows={5}
        value={draftAnswer}
        onChange={(e) => onAnswerChange(e.target.value)}
        placeholder="Antwort eingeben…"
        inputClassName="min-h-[7rem] text-base leading-relaxed"
      />

      {showPriceField ? (
        <TextInput
          label="Preis (optional)"
          inputMode="decimal"
          placeholder="z. B. 12,50"
          value={draftPrice}
          onChange={(e) => onPriceChange(e.target.value)}
          className="mt-3"
          inputClassName="text-base"
        />
      ) : null}

      <BillingAnswerPositionList
        positions={draftPositions}
        canSeePrices={canSeePrices}
        onAddArtikel={onAddArtikel}
        onAddLeistung={onAddLeistung}
        onSearchHistorie={onSearchHistorie}
        onEdit={onEditPosition}
        onRemove={onRemovePosition}
      />

      <div className="mt-4 flex flex-wrap justify-end gap-2">
        {editing && onCancel ? (
          <Button type="button" variant="secondary" disabled={busy} onClick={onCancel}>
            Abbrechen
          </Button>
        ) : null}
        <Button type="button" disabled={busy || !canSave} onClick={onSave}>
          {busy ? "Speichern…" : editing ? "Änderung speichern" : "Antwort speichern"}
        </Button>
      </div>
    </article>
  );
}

type PositionEditorState =
  | { rowKey: string; mode: "create" | "edit"; editIndex: number | null; position: EditorPosition }
  | null;

export function BillingQuestionAnswerDialog({
  open,
  onClose,
  timesheet,
  materials,
  onSaved,
  onShowPdf,
  onError,
}: {
  open: boolean;
  onClose: () => void;
  timesheet: TimesheetRecord;
  materials: TimesheetMaterialRecord[];
  onSaved: () => void;
  onShowPdf?: () => void;
  onError: (message: string) => void;
}) {
  const layers = useTimesheetFlowLayers();
  const { user } = useCurrentUser();
  const canSeePrices = userCanSeePrices(user);
  const timesheetId = timesheet.id;
  const auftragId = Number(timesheet.auftrag_id);
  const canProject = Number.isFinite(auftragId) && auftragId > 0;
  const docIdRaw = Number(timesheet.gd_rechnung_id ?? timesheet.invoice_document_id);
  const docId = Number.isFinite(docIdRaw) && docIdRaw > 0 ? docIdRaw : null;

  const [questions, setQuestions] = useState<BillingQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [answerDrafts, setAnswerDrafts] = useState<Record<string, string>>({});
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [positionDrafts, setPositionDrafts] = useState<Record<string, EditorPosition[]>>({});
  const [editingAnsweredRowKey, setEditingAnsweredRowKey] = useState<string | null>(null);
  const [actionLog, setActionLog] = useState<ActionLogEntry[]>([]);
  const [lohnarten, setLohnarten] = useState<GdLohnart[]>([]);
  const [kundenId, setKundenId] = useState<number | null>(
    Number.isFinite(Number(timesheet.kunden_id)) && Number(timesheet.kunden_id) > 0
      ? Number(timesheet.kunden_id)
      : null,
  );
  const [positionEditor, setPositionEditor] = useState<PositionEditorState>(null);
  const [historieRowKey, setHistorieRowKey] = useState<string | null>(null);
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);

  const visibleLohnarten = useMemo(
    () => filterGdLohnartenForUser(lohnarten, user),
    [lohnarten, user],
  );

  const timesheetSteps = useMemo<DocumentChainStep[]>(
    () => [
      {
        key: `stundennachweis-${timesheetId}`,
        kind: "stundennachweis",
        label: "Stundennachweis",
        timesheet_id: timesheetId,
        dokument_nummer: getTimesheetDisplayNumber(timesheet) || String(timesheetId),
        is_current: true,
      },
    ],
    [timesheet, timesheetId],
  );

  const loadQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const [list, log] = await Promise.all([
        listBillingQuestions(timesheetId, { includeAnswered: true }),
        getActionLog(timesheetId).catch(() => []),
      ]);
      setQuestions(list);
      setActionLog(log);
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Rückfragen konnten nicht geladen werden.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [timesheetId, onError]);

  useEffect(() => {
    if (!open) return;
    void loadQuestions();
  }, [open, loadQuestions]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void getGdLohnarten()
      .then((rows) => {
        if (!cancelled) setLohnarten(rows);
      })
      .catch(() => {
        if (!cancelled) setLohnarten([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !canProject) return;
    if (kundenId != null) return;
    let cancelled = false;
    void getOrder(auftragId)
      .then((order) => {
        if (cancelled) return;
        const id = Number(order.kunden_id);
        if (Number.isFinite(id) && id > 0) setKundenId(id);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, canProject, auftragId, kundenId]);

  useEffect(() => {
    if (open) return;
    setAnswerDrafts({});
    setPriceDrafts({});
    setPositionDrafts({});
    setEditingAnsweredRowKey(null);
    setSavingKey(null);
    setPositionEditor(null);
    setHistorieRowKey(null);
    setInvoicePreviewOpen(false);
  }, [open]);

  const allRows = useMemo(
    () => flattenBillingQuestionsToAnswerRows(questions, materials),
    [questions, materials],
  );
  const openRows = useMemo(() => allRows.filter((row) => row.isOpen), [allRows]);
  const answeredRows = useMemo(() => allRows.filter((row) => !row.isOpen), [allRows]);
  const openGeneralRows = useMemo(
    () => openRows.filter((row) => !row.materialLine),
    [openRows],
  );
  const openMaterialGroups = useMemo(
    () => groupMaterialAnswerRows(openRows.filter((row) => row.isOpen)),
    [openRows],
  );

  /** Follow-up / offene Material-Fragen: bestehende Unterpositionen zum Weiterbearbeiten vorbelegen. */
  useEffect(() => {
    if (!open || openRows.length === 0) return;
    setPositionDrafts((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const row of openRows) {
        if (next[row.key]?.length) continue;
        const priorAnswered =
          row.materialPositionId != null
            ? answeredRows.find(
                (answered) =>
                  answered.materialPositionId != null &&
                  Number(answered.materialPositionId) === Number(row.materialPositionId),
              )
            : answeredRows[0];
        const hydrated = hydrateAnswerPositions({
          materialPositionId: row.materialPositionId,
          respondedAt: priorAnswered?.respondedAt ?? null,
          materials,
          actionLog,
        });
        if (hydrated.length === 0) continue;
        next[row.key] = hydrated;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [open, openRows, answeredRows, materials, actionLog]);

  const displayNumber = getTimesheetDisplayNumber(timesheet) || String(timesheet.id);
  const metaParts = [
    timesheet.auftrag_display || timesheet.auftragsnummer || null,
    formatPeriod(timesheet) ? `Zeitraum: ${formatPeriod(timesheet)}` : null,
  ].filter(Boolean);

  const openNewPosition = (rowKey: string, typ: "artikel" | "leistung") => {
    const position = createBlockPosition(typ, 0, {
      lohnarten: typ === "leistung" ? visibleLohnarten : undefined,
    });
    setPositionEditor({ rowKey, mode: "create", editIndex: null, position });
  };

  const openEditPosition = (rowKey: string, index: number) => {
    const current = positionDrafts[rowKey]?.[index];
    if (!current) return;
    setPositionEditor({
      rowKey,
      mode: "edit",
      editIndex: index,
      position: current,
    });
  };

  const handlePositionEditorSave = (next: EditorPosition) => {
    if (!positionEditor) return;
    const { rowKey, editIndex } = positionEditor;
    setPositionDrafts((prev) => {
      const list = [...(prev[rowKey] ?? [])];
      if (editIndex != null && editIndex >= 0 && editIndex < list.length) {
        list[editIndex] = next;
      } else {
        list.push(next);
      }
      return { ...prev, [rowKey]: list };
    });
    setPositionEditor(null);
  };

  const handleHistorieInsert = (item: HistorischeDokumentPosition) => {
    if (!historieRowKey) return;
    const position = createPositionFromHistorie(item, 0);
    setHistorieRowKey(null);
    setPositionEditor({
      rowKey: historieRowKey,
      mode: "create",
      editIndex: null,
      position,
    });
  };

  const removePositionFromRow = (rowKey: string, index: number) => {
    setPositionDrafts((prev) => ({
      ...prev,
      [rowKey]: (prev[rowKey] ?? []).filter((_, i) => i !== index),
    }));
  };

  const saveRow = async (row: BillingAnswerRow) => {
    const draftPositions = positionDrafts[row.key] ?? [];
    const preservedArticleLines =
      draftPositions.length > 0
        ? draftPositions.map((position) => formatEditorPositionLine(position))
        : parseAnswerArticles(row.answerText);
    const answer = buildBillingAnswerText(answerDrafts[row.key] || "", preservedArticleLines);
    const typedPrice = canSeePrices ? (priceDrafts[row.key] || "").trim() : "";
    const priceRaw = typedPrice ? parseBillingPriceInput(typedPrice) : "";
    if (typedPrice && !priceRaw) {
      onError("Bitte einen gültigen Preis eingeben (z. B. 12,50).");
      return;
    }
    if (!answer && !priceRaw && draftPositions.length === 0) {
      onError(
        canSeePrices
          ? "Bitte eine Antwort, einen Preis oder mindestens eine Position eingeben."
          : "Bitte eine Antwort oder mindestens eine Position eingeben.",
      );
      return;
    }

    setSavingKey(row.key);
    try {
      if (draftPositions.length > 0) {
        const linkedMaterialId = row.materialPositionId ?? null;
        const siblingIds = resolveAnswerAppendedMaterialIds({
          respondedAt: row.respondedAt,
          actionLog,
        }).filter((id) => linkedMaterialId == null || id !== Number(linkedMaterialId));

        if (siblingIds.length > 0) {
          try {
            await deleteTimesheetMaterialPositions(timesheetId, siblingIds);
          } catch {
            // Merge trotzdem speichern – Alt-Geschwister können manuell bereinigt werden.
          }
        }

        if (linkedMaterialId != null) {
          const parentLabel =
            materials.find((material) => Number(material.id) === Number(linkedMaterialId))?.text ||
            row.materialLine ||
            null;
          if (draftPositions.length >= 2) {
            const merged = buildMergedLeistungFromPositions(draftPositions, parentLabel);
            const pricing = editorPositionPricingPayload(merged);
            try {
              await saveOutstandingMaterialPricing(timesheetId, linkedMaterialId, pricing);
            } catch {
              // Antwort trotzdem speichern
            }
          } else {
            const only = draftPositions[0];
            if (only) {
              const pricing = editorPositionPricingPayload(only);
              // Einzelposition: keine merged_children am Parent belassen.
              if (pricing.kalkulation_json && typeof pricing.kalkulation_json === "object") {
                const kalk = { ...pricing.kalkulation_json };
                const meta = kalk._gd_meta;
                if (meta && typeof meta === "object") {
                  const nextMeta = { ...(meta as Record<string, unknown>) };
                  delete nextMeta.merged_children;
                  if (Object.keys(nextMeta).length) kalk._gd_meta = nextMeta;
                  else delete kalk._gd_meta;
                }
                pricing.kalkulation_json = kalk;
              }
              try {
                await saveOutstandingMaterialPricing(timesheetId, linkedMaterialId, pricing);
              } catch {
                // optional
              }
            }
          }
        } else if (draftPositions.length >= 2) {
          const merged = buildMergedLeistungFromPositions(draftPositions);
          const material = editorPositionToMaterial(merged);
          const { ids } = await appendTimesheetMaterialPositions(timesheetId, [material]);
          const materialId = ids[0];
          if (materialId != null) {
            try {
              await saveOutstandingMaterialPricing(
                timesheetId,
                materialId,
                editorPositionPricingPayload(merged),
              );
            } catch {
              // optional
            }
          }
        } else {
          const only = draftPositions[0];
          if (only) {
            const { ids } = await appendTimesheetMaterialPositions(timesheetId, [
              editorPositionToMaterial(only),
            ]);
            const materialId = ids[0];
            if (materialId != null) {
              const pricing = editorPositionPricingPayload(only);
              if (pricing.einzelpreis_netto > 0 || pricing.kalkulation_json) {
                try {
                  await saveOutstandingMaterialPricing(timesheetId, materialId, pricing);
                } catch {
                  // optional
                }
              }
            }
          }
        }
      }

      if (row.itemId != null) {
        await respondBillingQuestion(timesheetId, row.questionId, {
          items: [
            {
              item_id: row.itemId,
              response_text: answer || null,
              price: priceRaw || null,
            },
          ],
        });
      } else {
        await respondBillingQuestion(timesheetId, row.questionId, {
          response_text: answer,
        });
      }
      setAnswerDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setPositionDrafts((prev) => {
        const next = { ...prev };
        delete next[row.key];
        return next;
      });
      setEditingAnsweredRowKey((current) => (current === row.key ? null : current));
      await loadQuestions();
      onSaved();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : "Antwort konnte nicht gespeichert werden.");
    } finally {
      setSavingKey(null);
    }
  };

  const startEditingAnswered = (row: BillingAnswerRow) => {
    const answerBody = answerTextWithoutArticles(row.answerText) || "";
    const priceValue =
      row.price != null && String(row.price).trim() !== ""
        ? String(row.price).replace(".", ",")
        : "";
    const hydrated = hydrateAnswerPositions({
      materialPositionId: row.materialPositionId,
      respondedAt: row.respondedAt,
      materials,
      actionLog,
    });

    setEditingAnsweredRowKey(row.key);
    setAnswerDrafts((prev) => ({ ...prev, [row.key]: answerBody }));
    setPriceDrafts((prev) => ({ ...prev, [row.key]: priceValue }));
    setPositionDrafts((prev) => ({ ...prev, [row.key]: hydrated }));
  };

  const cancelEditingAnswered = () => {
    if (!editingAnsweredRowKey) return;
    const key = editingAnsweredRowKey;
    setEditingAnsweredRowKey(null);
    setAnswerDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPriceDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setPositionDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderOpenAnswerCard = (row: BillingAnswerRow, editing = false) => (
    <OpenAnswerCard
      key={row.key}
      row={row}
      draftAnswer={answerDrafts[row.key] || ""}
      draftPrice={priceDrafts[row.key] || ""}
      draftPositions={positionDrafts[row.key] ?? []}
      busy={savingKey === row.key}
      editing={editing}
      canSeePrices={canSeePrices}
      onAnswerChange={(value) => setAnswerDrafts((prev) => ({ ...prev, [row.key]: value }))}
      onPriceChange={(value) => setPriceDrafts((prev) => ({ ...prev, [row.key]: value }))}
      onAddArtikel={() => openNewPosition(row.key, "artikel")}
      onAddLeistung={() => openNewPosition(row.key, "leistung")}
      onSearchHistorie={() => setHistorieRowKey(row.key)}
      onEditPosition={(index) => openEditPosition(row.key, index)}
      onRemovePosition={(index) => removePositionFromRow(row.key, index)}
      onSave={() => void saveRow(row)}
      onCancel={editing ? cancelEditingAnswered : undefined}
    />
  );

  const hasDrafts =
    Object.values(answerDrafts).some((v) => v.trim() !== "") ||
    Object.values(priceDrafts).some((v) => v.trim() !== "") ||
    Object.values(positionDrafts).some((list) => list.length > 0);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        dirty={hasDrafts}
        size="3xl"
        zIndexClass={layers.deep}
        fullScreenMobile
        panelClassName="flex flex-col sm:h-[min(94vh,58rem)] sm:max-h-[min(94vh,58rem)]"
        bodyClassName="min-h-0 flex-1 overflow-y-auto py-4 sm:py-5"
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span>{`Rückfragen beantworten – Stundennachweis ${displayNumber}`}</span>
            {canProject ? (
              <InvoiceDraftContextButtons
                auftragId={auftragId}
                docId={docId}
                timesheetSteps={timesheetSteps}
                buttonClassName="h-7 w-7 shrink-0"
              />
            ) : null}
          </span>
        }
        description={metaParts.length ? metaParts.join(" · ") : undefined}
        footer={
          <>
            <Button type="button" variant="secondary" onClick={onClose}>
              Schließen
            </Button>
            {onShowPdf ? (
              <Button type="button" variant="secondary" onClick={onShowPdf}>
                Stundennachweis anzeigen
              </Button>
            ) : null}
          </>
        }
      >
        {loading ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-8">
            <p className="text-sm leading-relaxed text-label-secondary">
              Beantworte jede Rückfrage einzeln. Optional kannst du Artikel oder Leistungen wie im
              Dokument hinterlegen bzw. frühere Positionen übernehmen. Gespeicherte Antworten
              verschwinden aus der Liste — offene Fragen bleiben bestehen, bis alle Teile beantwortet
              sind.
            </p>

            {openRows.length === 0 ? (
              <div className="rounded-ios-lg border border-status-green/30 bg-status-green/5 px-4 py-6 text-center">
                <p className="text-base font-medium text-label">Alle Rückfragen sind beantwortet.</p>
              </div>
            ) : null}

            {openMaterialGroups.length > 0 ? (
              <section className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-label-secondary">
                    Material-Rückfragen
                  </h3>
                  <p className="mt-1 text-sm text-label-tertiary">
                    {openMaterialGroups.reduce((sum, g) => sum + g.rows.length, 0)} offen
                  </p>
                </div>
                {openMaterialGroups.map((group) => (
                  <div key={group.materialLine} className="space-y-3">
                    <p className="rounded-ios bg-fill-tertiary px-3 py-2 text-sm font-semibold text-label">
                      {group.materialLine}
                    </p>
                    {group.rows.map((row) => renderOpenAnswerCard(row))}
                  </div>
                ))}
              </section>
            ) : null}

            {openGeneralRows.length > 0 ? (
              <section className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-label-secondary">
                    Allgemeine Rückfragen
                  </h3>
                  <p className="mt-1 text-sm text-label-tertiary">{openGeneralRows.length} offen</p>
                </div>
                <div className="space-y-4">
                  {openGeneralRows.map((row) => renderOpenAnswerCard(row))}
                </div>
              </section>
            ) : null}

            {openRows.length === 0 &&
            openMaterialGroups.length === 0 &&
            openGeneralRows.length === 0 &&
            !loading ? (
              <p className="text-sm text-label-secondary">Keine offenen Rückfragen.</p>
            ) : null}

            <section className="space-y-4 border-t border-separator pt-6">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-label-secondary">
                    Bereits beantwortet
                  </h3>
                  <p className="mt-1 text-sm text-label-tertiary">
                    {answeredRows.length > 0
                      ? `${answeredRows.length} Einträge`
                      : "Noch keine beantworteten Rückfragen."}
                  </p>
                </div>
                {answeredRows.length > 0 && canSeePrices ? (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setInvoicePreviewOpen(true)}
                  >
                    Rechnungsvorschau
                  </Button>
                ) : null}
              </div>
              {answeredRows.length > 0 ? (
                <div className={clsx("grid gap-3", answeredRows.length > 1 && "lg:grid-cols-2")}>
                  {answeredRows.map((row) => {
                    if (editingAnsweredRowKey === row.key) {
                      return (
                        <div key={row.key} className="lg:col-span-2">
                          {renderOpenAnswerCard(row, true)}
                        </div>
                      );
                    }
                    const articleDisplay = resolveAnsweredArticleDisplay({
                      materialPositionId: row.materialPositionId,
                      answerText: row.answerText,
                      respondedAt: row.respondedAt,
                      materials,
                      actionLog,
                      showPrices: canSeePrices,
                      formatMaterialLine: (material) =>
                        formatBillingMaterialLine(material as TimesheetMaterialRecord).line ||
                        null,
                    });
                    return (
                      <BillingAnsweredRowCard
                        key={row.key}
                        row={row}
                        canEdit
                        onEdit={startEditingAnswered}
                        articleLines={articleDisplay.lines}
                        unterpositionCount={articleDisplay.unterpositionCount}
                      />
                    );
                  })}
                </div>
              ) : null}
            </section>
          </div>
        )}
      </Dialog>

      <Dialog
        open={invoicePreviewOpen}
        onClose={() => setInvoicePreviewOpen(false)}
        size="3xl"
        zIndexClass={layers.deeper}
        fullScreenMobile
        panelClassName="flex flex-col sm:h-[min(94vh,58rem)] sm:max-h-[min(94vh,58rem)]"
        bodyClassName="min-h-0 flex-1 overflow-y-auto py-4 sm:py-5"
        title="Rechnungsvorschau"
        description="Vorschau der Material-/Antwortpositionen wie in der späteren Rechnung (interne Unterpositionen nur als Summe der Hauptposition)."
        footer={
          <Button type="button" variant="secondary" onClick={() => setInvoicePreviewOpen(false)}>
            Schließen
          </Button>
        }
      >
        <DocumentPreviewPanel
          embedded
          doc={buildBillingInvoicePreviewDoc(timesheet)}
          positions={buildBillingInvoicePreviewPositions(materials)}
        />
      </Dialog>

      <DocumentPositionEditDialog
        open={positionEditor != null}
        position={positionEditor?.position ?? null}
        editable
        currency="EUR"
        lohnarten={visibleLohnarten}
        kundenId={kundenId}
        auftragId={canProject ? auftragId : null}
        dokumentTyp="rechnung"
        onClose={() => setPositionEditor(null)}
        onSave={handlePositionEditorSave}
        zIndexClass={layers.deeper}
      />

      <DocumentHistorischePositionenDialog
        open={historieRowKey != null}
        onClose={() => setHistorieRowKey(null)}
        dokumentTyp="rechnung"
        auftragId={canProject ? auftragId : null}
        kundenId={kundenId}
        onInsert={handleHistorieInsert}
        zIndexClass={layers.deeper}
      />
    </>
  );
}
